import { motion } from "framer-motion";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { useState } from "react";
import { SignupModal } from "@/components/landing/SignupModal";
import { Users, Target, Lightbulb, Heart } from "lucide-react";

const values = [
  {
    icon: Users,
    title: "El profesional primero",
    description: "Cada decisión de producto se toma pensando en cómo liberar tiempo para que el nutricionista se concentre en su cliente.",
  },
  {
    icon: Target,
    title: "Precisión sin esfuerzo",
    description: "La tecnología debe ser invisible. Datos exactos, procesos automáticos, cero fricción.",
  },
  {
    icon: Lightbulb,
    title: "IA que potencia, no reemplaza",
    description: "La inteligencia artificial amplifica tus capacidades como profesional, nunca intenta sustituirte.",
  },
  {
    icon: Heart,
    title: "Adherencia a través de la experiencia",
    description: "Cuando el cliente ve su progreso de forma clara y profesional, la motivación y adherencia aumentan naturalmente.",
  },
];

const Nosotros = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header onOpenModal={() => setIsModalOpen(true)} />
      
      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4">
              Nosotros
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6">
              Creemos que el nutricionista{" "}
              <span className="text-gradient">debería escuchar, no documentar.</span>
            </h1>
          </motion.div>

          {/* Story */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert prose-lg max-w-none mb-20"
          >
            <div className="space-y-6 text-zinc-400 text-lg leading-relaxed">
              <p>
                Antropia nació de una frustración compartida: ver a nutricionistas excepcionales 
                perder horas valiosas documentando lo que sus oídos ya habían escuchado.
              </p>
              <p>
                Cada consulta nutricional genera información crítica: cambios de hábitos, 
                respuestas emocionales, datos de progreso, ajustes necesarios. Pero el profesional 
                está tan ocupado tomando notas que pierde matices importantes de la conversación.
              </p>
              <p>
                Nos preguntamos: ¿Y si la tecnología pudiera capturar todo automáticamente? 
                ¿Y si el nutricionista pudiera estar 100% presente con su cliente, sabiendo que 
                nada se pierde?
              </p>
              <p>
                Así nació Antropia: tu segundo cerebro para consultas nutricionales. Una plataforma 
                que graba, transcribe, analiza y documenta mientras tú te enfocas en lo que realmente 
                importa: la persona frente a ti.
              </p>
            </div>
          </motion.div>

          {/* Values */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="font-outfit text-2xl sm:text-3xl font-light text-center mb-12">
              Nuestros valores
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] hover:border-[#E05D3A]/30 transition-all duration-300"
                >
                  <div className="p-3 rounded-xl bg-[#E05D3A]/10 text-[#E05D3A] w-fit mb-4">
                    <value.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-outfit text-lg font-medium text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Team signature */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mt-20 pt-12 border-t border-white/10"
          >
            <p className="text-white font-outfit italic text-lg mb-2">
              — El equipo de Antropia
            </p>
            <p className="text-zinc-600 text-sm">
              Construyendo el futuro de las consultas nutricionales
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
      <SignupModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Nosotros;
